import React, { useEffect, useState } from 'react';
import FretboardScene from './components/Fretboard';
import TuningSelector from './components/TuningSelector';
import ScaleSelector from './components/ScaleSelector';
import FretSelector from './components/FretSelector';
import ChordSelector from './components/ChordSelector';
import ThemeSelector from './components/ThemeSelector';
import CapoSelector from './components/CapoSelector';
import useLocalStorage from './hooks/useLocalStorage';
import InstrumentSelector, { Instrument } from './components/InstrumentSelector';
import instruments from './data/instruments.json';
import RotationSelector from './components/RotationSelector';
import EmailOpener from './components/EmailOpener';
import { MobileTopNav } from './components/MobileTopNav';
import { MobileBottomNav } from './components/MobileBottomNav';
import ChordStepper from './components/ChordStepper';
import PianoKeys from './components/PianoKeys';
import { useIsPWA } from './hooks/useIsPWA';
import { CustomTuning } from './components/TuningSelector';
import { Scale, Chord, Note, Interval } from 'tonal';
import {
  logInstrumentSelection,
  logTuningSelection,
  logScaleSelection,
  logChordSelection,
  logCapoUsage,
  logCustomTuningCreated,
  logFretboardConfig,
  logNoteViewModeChange,
  logFeedbackRequest,
  logModalOpen,
  logSessionStart,
  logSessionDuration
} from './config/firebase';

const App: React.FC = () => {
  const defaultInstrument = instruments.find(inst => inst.name === '5 String Banjo');
  const defaultTuning = defaultInstrument?.tunings.find(tuning => tuning.name === defaultInstrument.defaultTuning)?.name || '';

  const [selectedInstrument, setSelectedInstrument] = useLocalStorage<string>('selectedInstrument', '5 String Banjo');
  const [tuning, setTuning] = useLocalStorage<string>('tuning', defaultTuning);
  const [selectedKey, setKey] = useLocalStorage<string>('selectedKey', 'G');
  const [scale, setScale] = useLocalStorage<string>('scale', 'major');
  const [noteViewMode, setNoteViewMode] = useLocalStorage<'letters' | 'scaleDegrees'>('noteViewMode', 'letters');
  const [frets, setFrets] = useLocalStorage<number>('frets', 12);
  const [showFretNumbers, setShowFretNumbers] = useLocalStorage<boolean>('showFretNumbers', true);
  const [selectedChord, setSelectedChord] = useLocalStorage<string>('selectedChord', '');
  const [hasDroneString, setHasDroneString] = useState(false);
  const [droneStringStartingFret, setDroneStringStartingFret] = useState(0);
  const [isLeftHanded, setIsLeftHanded] = useLocalStorage<boolean>('isLeftHanded', false);
  const [hideDroneStringNotes, setHideDroneStringNotes] = useState(false);
  const [useCapo, setUseCapo] = useLocalStorage<boolean>('useCapo', false);
  const [capoFret, setCapoFret] = useLocalStorage<number>('capoFret', 0);
  const [droneCapoFret, setDroneCapoFret] = useState<number>(0);
  const [rotation, setRotation] = useLocalStorage<number>('rotation', 0);
  const [showFretboardConfig, setShowFretboardConfig] = useLocalStorage<boolean>('showFretboardConfig', false);
  const [showPianoKeys, setShowPianoKeys] = useLocalStorage<boolean>('showPianoKeys', false);
  const [pianoOctaves, setPianoOctaves] = useLocalStorage<number>('pianoOctaves', 2);
  const [showPianoNoteLetters, setShowPianoNoteLetters] = useState(true);
  const [showNoteOctaves, setShowNoteOctaves] = useState(false);
  const [isChordDetectionMode, setIsChordDetectionMode] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [selectedChordName, setSelectedChordName] = useState<string | null>(null);
  const [hoveredChordName, setHoveredChordName] = useState<string | null>(null);

  const [activeModal, setActiveModal] = useState<'instrument' | 'scale' | 'chord' | 'settings' | null>(null);

  const isPWA = useIsPWA();

  const [customTunings, setCustomTunings] = useLocalStorage<CustomTuning[]>('customTunings', []);

  useEffect(() => {
    setSelectedChord('');
  }, [tuning, selectedKey, scale, setSelectedChord]);

  const instrument: Instrument | undefined = instruments.find(inst => inst.name === selectedInstrument);

  useEffect(() => {
    if (instrument) {
      const lastTuning = localStorage.getItem(`${instrument.name}_lastTuning`);
      if (lastTuning) {
        setTuning(lastTuning);
      } else {
        setTuning(instrument.defaultTuning);
      }
      setHasDroneString(instrument.hasDroneString);
      setDroneStringStartingFret(instrument.droneStringStartingFret);
      setHideDroneStringNotes(instrument.hideDroneStringNotes);
    }
  }, [selectedInstrument, setTuning, instrument]);

  useEffect(() => {
    if (hasDroneString && useCapo && capoFret > 0) {
      setDroneCapoFret(capoFret + droneStringStartingFret);
    } else {
      setDroneCapoFret(0);
    }
  }, [capoFret, hasDroneString, droneStringStartingFret, useCapo]);

  const currentTuningNotes = (() => {
    // First check standard tunings
    const standardTuning = instrument?.tunings.find(t => t.name === tuning);
    if (standardTuning) return standardTuning.notes;
    
    // Then check custom tunings
    const customTuning = customTunings.find(t => t.name === tuning && t.instrumentName === instrument?.name);
    return customTuning?.notes || [];
  })();

  const closeModal = () => setActiveModal(null);

  // Add analytics tracking to instrument selection
  const handleInstrumentChange = (newInstrument: string) => {
    logInstrumentSelection(newInstrument, selectedInstrument);
    setSelectedInstrument(newInstrument);
  };

  // Add analytics tracking to tuning changes
  const handleTuningChange = (newTuning: string) => {
    logTuningSelection(selectedInstrument, newTuning, tuning);
    setTuning(newTuning);
  };

  // Add analytics tracking to scale/key changes
  useEffect(() => {
    if (selectedKey && scale) {
      logScaleSelection(selectedKey, scale, selectedInstrument);
    }
  }, [selectedKey, scale, selectedInstrument]);

  // Add analytics tracking to chord selection
  useEffect(() => {
    if (selectedChord) {
      logChordSelection(selectedChord, selectedKey, scale, selectedInstrument);
      // Disable chord detection mode when a chord is selected
      if (isChordDetectionMode) {
        setIsChordDetectionMode(false);
        setSelectedNotes([]);
      }
    }
  }, [selectedChord, selectedKey, scale, selectedInstrument, isChordDetectionMode, setIsChordDetectionMode]);

  // Add analytics tracking to capo usage
  useEffect(() => {
    if (useCapo && capoFret > 0) {
      logCapoUsage(capoFret, selectedInstrument, tuning);
    }
  }, [useCapo, capoFret, selectedInstrument, tuning]);

  // Add analytics tracking to fretboard configuration changes
  useEffect(() => {
    logFretboardConfig({
      frets,
      isLeftHanded,
      rotation,
      showFretNumbers,
      instrumentName: selectedInstrument
    });
  }, [frets, isLeftHanded, rotation, showFretNumbers, selectedInstrument]);

  // Track custom tuning creation
  const handleCustomTuningCreated = (tunings: CustomTuning[]) => {
    setCustomTunings(tunings);
    // Get the newly added tuning by comparing with previous customTunings
    const newTuning = tunings.find(t => !customTunings.some(ct => ct.name === t.name && ct.instrumentName === t.instrumentName));
    if (newTuning) {
      logCustomTuningCreated(selectedInstrument, newTuning.name, newTuning.notes);
    }
  };

  // Track session duration
  useEffect(() => {
    const sessionStartTime = Date.now();
    logSessionStart();

    return () => {
      const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000);
      logSessionDuration(sessionDuration, selectedInstrument);
    };
  }, [selectedInstrument]);

  // Track note view mode changes
  const handleNoteViewModeChange = (mode: 'letters' | 'scaleDegrees') => {
    setNoteViewMode(mode);
    logNoteViewModeChange(mode, selectedInstrument);
  };

  // Track modal opens
  const handleModalOpen = (modalType: 'instrument' | 'scale' | 'chord' | 'settings') => {
    setActiveModal(modalType);
    logModalOpen(modalType, selectedInstrument);
  };

  // Track feedback requests
  const handleFeedbackClick = () => {
    logFeedbackRequest(selectedInstrument);
  };

  const detectChord = (notes: string[]): { name: string | null, analysis: string | null, alternatives: string[] } => {
    if (notes.length < 2) return { name: null, analysis: null, alternatives: [] };
    
    // Extract the actual notes from the position identifiers
    const actualNotes = notes.map(notePosition => {
      const [, , note] = notePosition.split('-');
      return note;
    });
    
    // Sort notes by MIDI number for proper pitch ordering
    const sortedNotes = [...actualNotes].sort((a, b) => {
      const midiA = Note.midi(a) || 0;
      const midiB = Note.midi(b) || 0;
      return midiA - midiB;
    });

    console.log('Sorted notes for chord detection:', sortedNotes);
    
    // Try detecting with each note as the root
    let bestChord = { name: null, analysis: null, alternatives: [] };
    let bestScore = 0;
    const allAlternatives = new Set<string>();

    for (const potentialRoot of sortedNotes) {
      // Rearrange notes to start with the potential root
      const rearrangedNotes = [
        potentialRoot,
        ...sortedNotes.filter(note => note !== potentialRoot)
      ];
      
      // Get all possible chord names for the given notes
      const possibleChords = Chord.detect(rearrangedNotes);
      console.log('Possible chords with root', potentialRoot, ':', possibleChords);

      if (possibleChords.length > 0) {
        // Score the chord based on how well it matches the notes
        const chordData = Chord.get(possibleChords[0]);
        const chordNotes = chordData.notes;
        const score = chordNotes.filter(note => 
          sortedNotes.some(sortedNote => Note.pitchClass(sortedNote) === Note.pitchClass(note))
        ).length;

        // Add all alternatives to the set
        possibleChords.forEach(chord => allAlternatives.add(chord));

        // Prefer non-slashed chords by adding a bonus to their score
        const isSlashedChord = possibleChords[0].includes('/');
        const adjustedScore = isSlashedChord ? score : score + 1;

        if (adjustedScore > bestScore) {
          bestScore = adjustedScore;
          bestChord = {
            name: possibleChords[0],
            analysis: generateChordAnalysis(possibleChords[0], rearrangedNotes),
            alternatives: []
          };
        }
      }
    }
    
    // Remove the best chord from alternatives and convert set to array
    allAlternatives.delete(bestChord.name);
    bestChord.alternatives = Array.from(allAlternatives);
    
    return bestChord;
  };

  // Helper function to generate chord analysis
  function generateChordAnalysis(chordName: string, sortedNotes: string[]): string {
    const chordData = Chord.get(chordName);
    
    // Create detailed explanation using the detected chord name
    let analysis = `This is a ${chordName} chord.\n\n`;
    
    // Get the root note from the chord name
    const rootNote = chordData.tonic;
    
    // Calculate intervals from the root note
    const intervals = sortedNotes.map(note => {
      const semitones = Interval.semitones(Interval.distance(rootNote, note)) || 0;
      let description = '';
      
      // Determine the interval description based on semitones
      if (semitones === 0) description = 'Root';
      else if (semitones === 1) description = 'Minor second';
      else if (semitones === 2) description = 'Major second';
      else if (semitones === 3) description = 'Minor third';
      else if (semitones === 4) description = 'Major third';
      else if (semitones === 5) description = 'Perfect fourth';
      else if (semitones === 6) description = 'Diminished fifth';
      else if (semitones === 7) description = 'Perfect fifth';
      else if (semitones === 8) description = 'Augmented fifth';
      else if (semitones === 9) description = 'Minor sixth';
      else if (semitones === 10) description = 'Minor seventh';
      else if (semitones === 11) description = 'Major seventh';
      else if (semitones === 12) description = 'Perfect octave';
      else if (semitones === 13) description = 'Minor ninth';
      else if (semitones === 14) description = 'Major ninth';
      else description = `${semitones} semitones from root`;
      
      return {
        note,
        semitones,
        description
      };
    });

    // Sort intervals to ensure root is first
    const sortedIntervals = [...intervals].sort((a, b) => a.semitones - b.semitones);

    // Add explanation based on chord type
    if (chordData.type.includes('diminished') || chordName.includes('dim') || chordName.includes('o')) {
      analysis += `Diminished chords have a tense, unstable sound. This one is built with:\n`;
      
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      analysis += `\nThe diminished fifth (a half-step below the perfect fifth) creates the characteristic tension in this chord. Diminished chords have a strong tendency to resolve to other chords, making them useful for creating movement in music.`;

      // Add explanation for major seventh in diminished chords
      if (chordName.includes('M7') || chordName.includes('maj7')) {
        analysis += `\n\nThe major seventh adds a sophisticated quality to the diminished chord. This creates a unique tension between the diminished quality and the major seventh, giving the chord a mysterious, dramatic character. This chord type is common in jazz and film music, often used to create a sense of tension or suspense.`;
      }
    } else if (chordData.type.includes('suspended') || chordName.includes('sus')) {
      analysis += `Suspended chords have a bright, open sound. This one is built with:\n`;
      
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      if (chordName.includes('sus2')) {
        analysis += `\nThis is a sus2 chord, where the third is replaced with a major second. This creates a bright, open sound that can resolve to either a major or minor chord. The second adds a sense of anticipation and movement.`;
      } else if (chordName.includes('sus4')) {
        analysis += `\nThis is a sus4 chord, where the third is replaced with a perfect fourth. This creates a suspended, floating quality that typically resolves down to the third. The fourth adds tension and a sense of suspension.`;
      }

      // Add explanation for dominant 7th in suspended chords
      if (chordName.includes('7')) {
        analysis += `\n\nThe minor seventh adds a dominant quality to the suspended chord, creating a strong tendency to resolve. This combination of the suspended quality with the dominant seventh creates a unique tension that's common in jazz and contemporary music. The suspended quality adds ambiguity while the dominant seventh adds direction.`;
      }

      // Add explanations for additional chord characteristics
      if (chordName.includes('b5')) {
        analysis += `\n\nThe diminished fifth adds a distinctive tension to the suspended chord. This creates a more complex, unstable sound that's common in jazz and contemporary music. The combination of the suspended quality with the diminished fifth creates a unique, floating tension.`;
      }

      if (chordName.includes('6')) {
        analysis += `\n\nThe added sixth creates a rich, jazzy quality to the chord. This is a common extension in jazz and contemporary music that adds color and complexity while maintaining the suspended character of the chord.`;
      }

      if (chordName.includes('M7')) {
        analysis += `\n\nThe major seventh adds a rich, jazzy quality to the chord. It creates a sophisticated sound that's common in jazz and contemporary music.`;
      }
      if (chordName.includes('#5')) {
        analysis += `\n\nThe augmented fifth is an interval that is one half-step larger than a perfect fifth. While a perfect fifth adds stability to the chord, the augmented fifth creates tension and brightness. In this minor chord, this tension creates an interesting contrast with the minor third's sad quality, giving the chord a unique, dramatic character.`;
      }
    } else if (chordName.includes('m7')) {
      analysis += `Minor 7th chords have a mellow, jazzy sound. This one is built with:\n`;
      
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      analysis += `\nThe minor third and minor seventh create the characteristic minor 7th sound. This chord type is common in jazz and creates a smooth, sophisticated sound. The minor third gives it its sad quality, while the minor seventh adds a jazzy flavor.`;
    } else if (chordData.type.includes('major') || chordName.includes('M')) {
      analysis += `This major chord contains:\n`;
      sortedIntervals.forEach(({ note, description }) => {
        // Convert augmented fifth to flat sixth for major chords with b6
        if (description === 'Augmented fifth' && chordName.includes('b6')) {
          analysis += `• Flat sixth (${note}): Adds a distinctive, jazzy quality to the chord\n`;
        } else {
          analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
        }
      });
      
      analysis += `\nThe major third is what gives this chord its characteristic bright sound. It's 4 half-steps above the root.`;

      // Add explanation for major seventh
      if (chordName.includes('maj7') || chordName.includes('M7')) {
        analysis += `\n\nThe major seventh is an interval that is one half-step larger than a minor seventh. While a minor seventh adds a dominant, bluesy quality, the major seventh creates a more stable, dreamy sound. This interval gives the chord a sophisticated, jazzy character. The major seventh creates a gentle tension that adds color without demanding resolution.`;

      }

      // Add explanation for flat sixth
      if (chordName.includes('b6')) {
        analysis += `\n\nThe flat sixth is an interval that is one half-step smaller than a major sixth. While a major sixth adds a bright, consonant quality, the flat sixth creates a distinctive, jazzy tension. This interval gives the chord a sophisticated, modern character.`;
      }

      // Add explanation for augmented fifth
      if (chordName.includes('#5')) {
        analysis += `\n\nThe augmented fifth is an interval that is one half-step larger than a perfect fifth. While a perfect fifth adds stability to the chord, the augmented fifth creates tension and brightness. In this minor chord, this tension creates an interesting contrast with the minor third's sad quality, giving the chord a unique, dramatic character.`;
      }

      // Add explanation for minor ninth
      if (chordName.includes('b9')) {
        analysis += `\n\nThe minor ninth is an interval that is one half-step smaller than a major ninth. While a major ninth adds a rich, complex quality, the minor ninth creates a distinctive tension. This interval is often used to create a spicy, dissonant sound in jazz and contemporary music.`;
      }

      // Add explanation for added ninth
      if (chordName.includes('add9')) {
        analysis += `\n\nThe added ninth is an interval that is one octave plus a major second above the root. While a major second adds a bright, open quality, the ninth adds a layer of complexity while maintaining the chord's basic character. This interval is common in jazz and contemporary music, adding color without the complexity of a full ninth chord.`;
      }
    } else if (chordData.type.includes('minor') || chordName.includes('m')) {
      analysis += `This minor chord contains:\n`;
      
      sortedIntervals.forEach(({ note, description }) => {
        // Convert augmented fifth to flat sixth for minor chords with b6
        if (description === 'Augmented fifth' && chordName.includes('b6')) {
          analysis += `• Flat sixth (${note}): Adds a distinctive, jazzy quality to the chord\n`;
        }
        // Convert minor second to flat ninth for minor chords with b9
        else if (description === 'Minor second' && chordName.includes('b9')) {
          analysis += `• Flat ninth (${note}): Creates a distinctive tension in the chord\n`;
        }
        else {
          analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
        }
      });
      
      analysis += `\nThe minor third is what gives this chord its characteristic sad sound. It's 3 half-steps above the root.`;

      // Add explanation for augmented fifth
      if (chordName.includes('#5')) {
        analysis += `\n\nThe augmented fifth (a half-step above the perfect fifth) creates a distinctive tension in the chord. It's a half-step higher than a perfect fifth and adds a bright, unstable quality that contrasts with the minor third. This creates a unique, dramatic sound that's common in jazz and film music. The combination of the minor third's sadness with the augmented fifth's brightness creates a complex emotional character.`;
      }

      // Add explanation for flat sixth
      if (chordName.includes('b6')) {
        analysis += `\n\nThe flat sixth creates a distinctive, jazzy quality to the chord. This is a common alteration in jazz and contemporary music that adds a rich, complex sound. The flat sixth creates a unique tension that gives the chord a sophisticated, modern character.`;
      }

      // Add explanation for flat ninth
      if (chordName.includes('b9')) {
        analysis += `\n\nThe flat ninth creates a distinctive tension in the chord. This is a common alteration in jazz and contemporary music that adds a dissonant, spicy quality. The flat ninth creates a strong clash with the root note, adding complexity and color to the chord.`;
      }

      // Add explanation for added fourth
      if (chordData.type.includes('add4') || chordName.includes('add4')) {
        analysis += `\n\nThe added fourth is an interval that is one half-step larger than a major third. While a major third defines the chord's major quality, the added fourth creates a suspended quality, adding tension and interest to the chord.`;
      }
      // Add explanation for added ninth
      else if (chordData.type.includes('add9') || chordName.includes('add9')) {
        analysis += `\n\nThe added ninth (major second) adds a bright, open quality to the chord.`;
      }
    } else if (chordData.type.includes('dominant') || chordName.includes('7') || chordName.includes('9')) {
      analysis += `This dominant 7th chord contains:\n`;
      
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      if (chordName.includes('9')) {
        analysis += `\nThis is a dominant 9th chord, which combines the tension of a dominant 7th with the added color of a major ninth. The major third and minor seventh create the characteristic dominant sound, while the ninth adds a rich, complex quality.`;
      } else {
        analysis += `\nThe major third and minor seventh create the characteristic dominant 7th sound, which has a strong tendency to resolve to other chords.`;
      }
      
      if (chordName.includes('#5')) {
        analysis += `\n\nThe augmented fifth (a half-step above the perfect fifth) creates additional tension and a more complex sound. This is a common alteration in jazz and blues, where the augmented fifth adds a distinctive, bright quality that intensifies the chord's tendency to resolve. The combination of the augmented fifth with the minor seventh creates a particularly rich, colorful sound.`;
      }
      
      if (chordName.includes('b5')) {
        analysis += `\n\nThe diminished fifth (a half-step below the perfect fifth) creates additional tension in the chord. This is a common alteration in jazz and blues, where the diminished fifth adds a distinctive, unstable quality that intensifies the chord's tendency to resolve. The combination of the diminished fifth with the minor seventh creates a particularly tense sound that's often used for dramatic effect.`;
      }
      
      if (chordName.includes('no5')) {
        analysis += `\n\nThis chord omits the fifth, creating a more open, ambiguous sound that's common in jazz and contemporary music.`;
      }
    } else if (chordName.includes('aug') || chordName.includes('#5')) {
      analysis += `This augmented chord contains:\n`;
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      analysis += `\nThe augmented fifth (a half-step above the perfect fifth) creates the characteristic bright, tense sound of this chord. Augmented chords have a distinctive, unstable quality that creates a sense of movement and tension. The combination of the major third with the augmented fifth gives the chord its unique, dramatic character.`;
    } else {
      // Default to power chord if no other type is detected
      analysis += `Power chords (also called 5 chords) have a strong, neutral sound. This one is built with:\n`;
      sortedIntervals.forEach(({ note, description }) => {
        analysis += `• ${description} (${note}): ${getIntervalRole(description)}\n`;
      });
      
      analysis += `\nPower chords are commonly used in rock and metal music. They have a neutral sound because they don't include a third, making them neither major nor minor.`;
    }

    // Add explanation for slash chord if present
    if (chordData.bass && chordData.bass !== chordData.tonic) {
      analysis += `\n\nThis is a slash chord (${chordData.bass} in the bass). The bass note ${chordData.bass} is played as the lowest note, creating a different voicing of the chord. This can add interesting harmonic movement or create a specific bass line.`;
    }

    // Add explanation for diminished fifth
    if (chordName.includes('b5')) {
      analysis += `\n\nThe diminished fifth is an interval that is one half-step smaller than a perfect fifth. While a perfect fifth adds stability to the chord, the diminished fifth creates tension and instability. This interval is also known as a tritone, and its dissonant quality gives the chord a distinctive, unresolved character.`;
    }

    // Add explanation for augmented fourth
    if (chordName.includes('#4')) {
      analysis += `\n\nThe augmented fourth is an interval that is one half-step larger than a perfect fourth. While a perfect fourth adds a stable, consonant quality, the augmented fourth creates tension and dissonance. This interval is also known as a tritone, and its unstable quality gives the chord a distinctive, unresolved character.`;
    }

    // Add explanation for diminished seventh
    if (chordName.includes('dim7')) {
      analysis += `\n\nThe diminished seventh is an interval that is one half-step smaller than a minor seventh. While a minor seventh adds a jazzy quality, the diminished seventh creates additional tension and instability. This interval helps create the characteristic tense, unresolved sound of diminished chords.`;
    }

    // Add explanation for augmented ninth
    if (chordName.includes('#9')) {
      analysis += `\n\nThe augmented ninth is an interval that is one half-step larger than a major ninth. While a major ninth adds a rich, complex quality, the augmented ninth creates additional tension and dissonance. This interval is often used to create a bluesy, expressive sound.`;
    }

    return analysis;
  }

  // Helper function to get the role of each interval in the chord
  function getIntervalRole(description: string): string {
    if (description === 'Root') return 'The foundation of the chord, giving it its name and stability';
    if (description.includes('third')) return 'Creates the chord\'s quality (major or minor)';
    if (description.includes('fifth')) return 'Adds strength and stability to the chord';
    if (description.includes('seventh')) return 'Adds a jazzy, bluesy quality';
    if (description.includes('ninth')) return 'Adds a rich, complex quality';
    if (description.includes('eleventh')) return 'Adds a dreamy, suspended quality';
    if (description.includes('thirteenth')) return 'Adds a lush, complex quality';
    return 'Adds additional color to the chord';
  }

  const getActiveNotes = () => {
    const notes: string[] = [];
    if (selectedChord) {
      const chordNotes = Chord.get(selectedChord).notes;
      // Get all possible enharmonic equivalents for each note
      chordNotes.forEach(note => {
        const noteWithoutOctave = note.replace(/\d+$/, '');
        notes.push(noteWithoutOctave);
        let currentNote = noteWithoutOctave;
        while (true) {
          const nextEnharmonic = Note.enharmonic(currentNote);
          if (nextEnharmonic === currentNote || notes.includes(nextEnharmonic)) {
            break;
          }
          notes.push(nextEnharmonic);
          currentNote = nextEnharmonic;
        }
      });
    } else if (selectedKey && scale) {
      const scaleNotes = Scale.get(`${selectedKey} ${scale}`).notes;
      // Get all possible enharmonic equivalents for each note
      scaleNotes.forEach(note => {
        const noteWithoutOctave = note.replace(/\d+$/, '');
        notes.push(noteWithoutOctave);
        let currentNote = noteWithoutOctave;
        while (true) {
          const nextEnharmonic = Note.enharmonic(currentNote);
          if (nextEnharmonic === currentNote || notes.includes(nextEnharmonic)) {
            break;
          }
          notes.push(nextEnharmonic);
          currentNote = nextEnharmonic;
        }
      });
    }
    return notes;
  };

  // Update selectedChordName when notes change
  useEffect(() => {
    if (selectedNotes.length > 0) {
      const detected = detectChord(selectedNotes);
      setSelectedChordName(detected.name);
    } else {
      setSelectedChordName(null);
    }
  }, [selectedNotes]);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-base-200 overflow-hidden">
      <div className="flex-grow flex flex-col md:flex-row overflow-hidden">
        <MobileTopNav setActiveModal={handleModalOpen} />
        <div className="flex-grow flex flex-col items-center justify-between bg-base-200 overflow-hidden">
          <div className="w-full h-full flex flex-col items-center min-h-0">
            <div className="w-full flex-1 flex items-center justify-center min-h-0">
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-full h-full flex items-center justify-center">
                  <FretboardScene
                    tuning={currentTuningNotes}
                    selectedKey={selectedKey}
                    selectedScale={scale}
                    selectedChord={selectedChord}
                    numberOfFrets={frets}
                    hasDroneString={hasDroneString}
                    droneStringStartingFret={droneStringStartingFret}
                    isLeftHanded={isLeftHanded}
                    hideDroneStringNotes={hideDroneStringNotes}
                    showFretNumbers={showFretNumbers}
                    capoFret={useCapo ? capoFret : 0}
                    droneCapoFret={useCapo ? droneCapoFret : 0}
                    useCapo={useCapo}
                    rotation={rotation * -1}
                    noteViewMode={noteViewMode}
                    isChordDetectionMode={isChordDetectionMode}
                    selectedNotes={selectedNotes}
                    setSelectedNotes={setSelectedNotes}
                    showNoteOctaves={showNoteOctaves}
                  />
                </div>
              </div>
            </div>
            {showPianoKeys && (
              <div className="w-full flex-shrink-0 mb-0 flex items-center">
                <PianoKeys activeNotes={getActiveNotes()} octaves={pianoOctaves} showNoteLetters={showPianoNoteLetters} />
              </div>
            )}
          </div>
          <div className={`w-full flex-shrink-0 md:hidden ${isPWA ? 'mb-[5.5rem]' : 'mb-[4.25rem]'} ${showPianoKeys ? 'mt-0' : 'mt-[-.6rem]'}`}>
            <ChordStepper
              selectedKey={selectedKey}
              scale={scale}
              selectedChord={selectedChord}
              setSelectedChord={setSelectedChord}
              closeModal={closeModal}
              showNumerals={true}
              vertical={false}
              instrumentName={selectedInstrument}
            />
          </div>
        </div>
      </div>
      
      <MobileBottomNav
        setActiveModal={handleModalOpen}
        selectedInstrument={selectedInstrument}
        tuningNotes={currentTuningNotes}
        capoFret={capoFret}
        useCapo={useCapo}
        selectedKey={selectedKey}
        scale={scale}
        selectedChord={selectedChord}
        frets={frets}
        rotation={rotation}
        isLeftHanded={isLeftHanded}
      />
      

      <div className="w-full md:w-1/2 lg:w-1/2 xl:w-1/3 bg-base-100 overflow-y-auto hidden md:block">
        <div className="p-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">
              String Wizard <span className="indicator-item badge badge-primary h-full align-middle">Beta</span>
            </h1>
            <ThemeSelector />
          </div>

          <div className="space-y-4">
            <InstrumentSelector
              selectedInstrument={selectedInstrument}
              setSelectedInstrument={handleInstrumentChange}
              setTuning={handleTuningChange}
              setHasDroneString={setHasDroneString}
              setDroneStringStartingFret={setDroneStringStartingFret}
              isLeftHanded={isLeftHanded}
              setIsLeftHanded={setIsLeftHanded}
              setHideDroneStringNotes={setHideDroneStringNotes}
            />
            <div className="bg-base-200 collapse">
              <input type="checkbox" className="peer" checked={showFretboardConfig}
                onChange={(e) => setShowFretboardConfig(e.target.checked)} />
              <div className="collapse-title bg-base-200 px-4 w-full flex items-center justify-center">
                <div className="flex w-full text-xs items-center justify-center">
                  <span className='text-center'>{`${isLeftHanded ? "Left Handed" : "Right Handed"}`}</span>
                  <div className="divider mx-0.5 divider-horizontal"></div>
                  <span className='text-center'>{`${rotation === 0 ? "Vertical Fretboard" : rotation == -90 || rotation == 90 ? "Horizontal Fretboard" : "Rotation: " + rotation}`}</span>
                  <div className="divider mx-0.5 divider-horizontal"></div>
                  <span className='text-center'>{`${frets} Frets`}</span>
                  <div className="divider mx-0.5 divider-horizontal"></div>
                  <span className='text-center'>{`${showFretNumbers ? "Visible Fret Numbers" : "Hidden Fret Numbers"}`}</span>
                </div>
              </div>
              <div className="collapse-content bg-base-200 ">
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Left Handed</span>
                    <input
                      type="checkbox"
                      className="toggle"
                      checked={isLeftHanded}
                      onChange={(e) => setIsLeftHanded(e.target.checked)}
                    />
                  </label>
                </div>
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Chord Detection Mode</span>
                    <input
                      type="checkbox"
                      className="toggle"
                      checked={isChordDetectionMode}
                      onChange={(e) => {
                        setIsChordDetectionMode(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedNotes([]);
                        } else {
                          // Clear selectedChord when enabling chord detection mode
                          setSelectedChord('');
                        }
                      }}
                    />
                  </label>
                </div>
                {isChordDetectionMode && selectedNotes.length === 0 && (
                  <div className="mt-2 p-3 bg-base-300 rounded-lg text-center">
                    <div className="text-sm">
                      <p className="font-semibold mb-1">Chord Detection Mode Active</p>
                      <p>Tap or click notes on the fretboard to select them. The app will identify possible chord matches based on your selection.</p>
                    </div>
                  </div>
                )}
                {isChordDetectionMode && selectedNotes.length > 0 && (
                  <div className="mt-2 p-2 bg-base-300 rounded-lg">
                    <div className="text-sm font-semibold">Selected Notes:</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {[...selectedNotes]
                        .map(notePosition => {
                          const [, , note] = notePosition.split('-');
                          return note;
                        })
                        .sort((a, b) => {
                          const midiA = Note.midi(a) || 0;
                          const midiB = Note.midi(b) || 0;
                          return midiA - midiB;
                        })
                        .map((note, index) => (
                          <div key={index} className="badge badge-primary">
                            {showNoteOctaves ? `${Note.pitchClass(note)}${Note.octave(note)}` : Note.pitchClass(note)}
                          </div>
                        ))}
                    </div>
                    {detectChord(selectedNotes).name && (
                      <div className="mt-2">
                        <div className="text-sm font-semibold">Detected Chord:</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <button
                            className={`badge ${selectedChordName === detectChord(selectedNotes).name ? 'badge-secondary' : 'badge-outline hover:bg-primary hover:text-primary-content'} cursor-pointer`}
                            onClick={() => {
                              setSelectedChordName(detectChord(selectedNotes).name);
                              const chordDisplay = document.getElementById('chord-analysis');
                              if (chordDisplay) {
                                chordDisplay.innerHTML = detectChord(selectedNotes).analysis;
                              }
                            }}
                            onMouseEnter={() => {
                              setHoveredChordName(detectChord(selectedNotes).name);
                              const chordDisplay = document.getElementById('chord-analysis');
                              if (chordDisplay) {
                                chordDisplay.innerHTML = detectChord(selectedNotes).analysis;
                              }
                            }}
                            onMouseLeave={() => {
                              setHoveredChordName(null);
                              const chordDisplay = document.getElementById('chord-analysis');
                              if (chordDisplay) {
                                chordDisplay.innerHTML = selectedChordName ? 
                                  generateChordAnalysis(selectedChordName, selectedNotes.map(notePosition => {
                                    const [, , note] = notePosition.split('-');
                                    return note;
                                  })) : 
                                  detectChord(selectedNotes).analysis;
                              }
                            }}
                          >
                            {detectChord(selectedNotes).name}
                          </button>
                          {detectChord(selectedNotes).alternatives.map((altChord, index) => (
                            <button
                              key={index}
                              className={`badge ${selectedChordName === altChord ? 'badge-secondary' : 'badge-outline opacity-50 hover:opacity-100 hover:bg-primary hover:text-primary-content'} cursor-pointer`}
                              onClick={() => {
                                setSelectedChordName(altChord);
                                const chordDisplay = document.getElementById('chord-analysis');
                                if (chordDisplay) {
                                  chordDisplay.innerHTML = generateChordAnalysis(altChord, selectedNotes.map(notePosition => {
                                    const [, , note] = notePosition.split('-');
                                    return note;
                                  }));
                                }
                              }}
                              onMouseEnter={() => {
                                setHoveredChordName(altChord);
                                const chordDisplay = document.getElementById('chord-analysis');
                                if (chordDisplay) {
                                  chordDisplay.innerHTML = generateChordAnalysis(altChord, selectedNotes.map(notePosition => {
                                    const [, , note] = notePosition.split('-');
                                    return note;
                                  }));
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredChordName(null);
                                const chordDisplay = document.getElementById('chord-analysis');
                                if (chordDisplay) {
                                  chordDisplay.innerHTML = selectedChordName ? 
                                    generateChordAnalysis(selectedChordName, selectedNotes.map(notePosition => {
                                      const [, , note] = notePosition.split('-');
                                      return note;
                                    })) : 
                                    detectChord(selectedNotes).analysis;
                                }
                              }}
                            >
                              {altChord}
                            </button>
                          ))}
                        </div>
                        <div id="chord-analysis" className="text-sm mt-2 text-base-content/80 leading-relaxed whitespace-pre-line">
                          {detectChord(selectedNotes).analysis}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <RotationSelector
                  rotation={rotation}
                  setRotation={setRotation}
                />
                <FretSelector
                  frets={frets}
                  setFrets={setFrets}
                  showFretNumbers={showFretNumbers}
                  setShowFretNumbers={setShowFretNumbers}
                />
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Show Note Octaves</span>
                    <input
                      type="checkbox"
                      className="toggle"
                      checked={showNoteOctaves}
                      onChange={(e) => setShowNoteOctaves(e.target.checked)}
                    />
                  </label>
                </div>
                <CapoSelector
                  useCapo={useCapo}
                  setUseCapo={setUseCapo}
                  capoFret={capoFret}
                  setCapoFret={setCapoFret}
                  maxFrets={frets}
                />
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Show Piano Keys</span>
                    <input
                      type="checkbox"
                      className="toggle"
                      checked={showPianoKeys}
                      onChange={(e) => setShowPianoKeys(e.target.checked)}
                    />
                  </label>
                  {showPianoKeys && (
                    <div className="bg-base-200 mt-1 pl-4">
                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Show Notes on Keys</span>
                          <input
                            type="checkbox"
                            className="toggle"
                            checked={showPianoNoteLetters}
                            onChange={(e) => setShowPianoNoteLetters(e.target.checked)}
                          />
                        </label>
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Piano Octaves</span>
                          <span className="label-text-alt">{pianoOctaves}</span>
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          value={pianoOctaves}
                          onChange={(e) => setPianoOctaves(parseInt(e.target.value))}
                          className="range range-primary"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {instrument && (
              <TuningSelector
                tuning={tuning}
                setTuning={handleTuningChange}
                instrument={instrument}
                customTunings={customTunings}
                setCustomTunings={handleCustomTuningCreated}
              />
            )}
            <ScaleSelector
              selectedKey={selectedKey}
              scale={scale}
              setKey={setKey}
              setScale={setScale}
              selectedChord={selectedChord}
              noteViewMode={noteViewMode}
              setNoteViewMode={handleNoteViewModeChange}
            />
            <ChordSelector
              selectedKey={selectedKey}
              scale={scale}
              selectedChord={selectedChord}
              setSelectedChord={setSelectedChord}
            />

            <div className="divider divider-vertical"></div>
            <div className='flex items-end'>
              <EmailOpener address="info@stringwizard.app" subject="String Wizard Feedback or Feature Request" body="">
                <button 
                  name='emailFeedback' 
                  className="btn btn-xl btn-secondary w-80 mb-4 ml-auto mr-auto"
                  onClick={handleFeedbackClick}
                >
                  Leave Feedback or Request
                </button>
              </EmailOpener>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Modals */}
      <dialog id="modal_instrument" className={`modal modal-bottom sm:modal-middle ${activeModal === 'instrument' ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg" onClick={closeModal}>Instrument Settings</h3>
          <InstrumentSelector
            selectedInstrument={selectedInstrument}
            setSelectedInstrument={handleInstrumentChange}
            setTuning={handleTuningChange}
            setHasDroneString={setHasDroneString}
            setDroneStringStartingFret={setDroneStringStartingFret}
            isLeftHanded={isLeftHanded}
            setIsLeftHanded={setIsLeftHanded}
            setHideDroneStringNotes={setHideDroneStringNotes}
          />
          {instrument && (
            <TuningSelector
              tuning={tuning}
              setTuning={handleTuningChange}
              instrument={instrument}
              customTunings={customTunings}
              setCustomTunings={handleCustomTuningCreated}
            />
          )}
          <CapoSelector
            useCapo={useCapo}
            setUseCapo={setUseCapo}
            capoFret={capoFret}
            setCapoFret={setCapoFret}
            maxFrets={frets}
          />
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
            </form>
          </div>
          <button className="btn w-full" onClick={closeModal}>Close</button>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeModal}>close</button>
        </form>
      </dialog>

      <dialog id="modal_scale" className={`modal modal-bottom sm:modal-middle ${activeModal === 'scale' ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg" onClick={closeModal}>Scale Settings</h3>
          <ScaleSelector
            selectedKey={selectedKey}
            scale={scale}
            setKey={setKey}
            setScale={setScale}
            selectedChord={selectedChord}
            noteViewMode={noteViewMode}
            setNoteViewMode={handleNoteViewModeChange}
          />
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
            </form>
          </div>
          <button className="btn w-full" onClick={closeModal}>Close</button>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeModal}>close</button>
        </form>
      </dialog>

      <dialog id="modal_chord" className={`modal modal-bottom sm:modal-middle ${activeModal === 'chord' ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg" onClick={closeModal}>Chords</h3>
          <ChordSelector
            selectedKey={selectedKey}
            scale={scale}
            selectedChord={selectedChord}
            setSelectedChord={setSelectedChord}
            compact={true}
            closeModal={closeModal}
          />
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
            </form>
          </div>
          <button className="btn w-full" onClick={closeModal}>Close</button>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeModal}>close</button>
        </form>
      </dialog>

      <dialog id="modal_settings" className={`modal modal-bottom sm:modal-middle ${activeModal === 'settings' ? 'modal-open' : ''}`}>
            <div className="modal-box">
              <h3 className="font-bold text-lg" onClick={closeModal}>Settings</h3>
              <ThemeSelector asSelectInput={true} />
              <div className='divider divider-vertical'></div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Left Handed</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={isLeftHanded}
                    onChange={(e) => setIsLeftHanded(e.target.checked)}
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Show Piano Keys</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={showPianoKeys}
                    onChange={(e) => setShowPianoKeys(e.target.checked)}
                  />
                </label>
              </div>
              <RotationSelector
                rotation={rotation}
                setRotation={setRotation}
              />
              <FretSelector
                frets={frets}
                setFrets={setFrets}
                showFretNumbers={showFretNumbers}
                setShowFretNumbers={setShowFretNumbers}
              />
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Show Note Octaves</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={showNoteOctaves}
                    onChange={(e) => setShowNoteOctaves(e.target.checked)}
                  />
                </label>
              </div>
              <CapoSelector
                useCapo={useCapo}
                setUseCapo={setUseCapo}
                capoFret={capoFret}
                setCapoFret={setCapoFret}
                maxFrets={frets}
              />
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Show Piano Keys</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={showPianoKeys}
                    onChange={(e) => setShowPianoKeys(e.target.checked)}
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Show Notes on Keys</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={showPianoNoteLetters}
                    onChange={(e) => setShowPianoNoteLetters(e.target.checked)}
                  />
                </label>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Piano Octaves</span>
                  <span className="label-text-alt">{pianoOctaves}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="4"
                  value={pianoOctaves}
                  onChange={(e) => setPianoOctaves(parseInt(e.target.value))}
                  className="range range-primary"
                />
              </div>
              <div className="modal-action">
                <form method="dialog">
                  {/* if there is a button in form, it will close the modal */}
                </form>
              </div>
              <button className="btn w-full" onClick={closeModal}>Close</button>
            </div>
            <form method="dialog" className="modal-backdrop">
              <button onClick={closeModal}>close</button>
            </form>
          </dialog>
        </div>
        );
};

        export default App;